import { create } from "zustand";
import { combine, persist, createJSONStorage, PersistStorage, StorageValue } from "zustand/middleware";

export const COOKIE_PREFERENCES_KEY = "sol:cookies";

const DEFAULT_STATE = {
    isPolicyAccepted: false,
    cookies: {
        application: { disabled: true, value: true },
        analytics: { disabled: false, value: true },
    },
};

export type CookieKind = keyof typeof DEFAULT_STATE.cookies;

export function createInterruptibleStorage<S>(
    getStorage: () => PersistStorage<S> | undefined,
    options?: { interrupt?: (name: string, value: StorageValue<S>) => boolean },
): PersistStorage<S> | undefined {
    let storage: PersistStorage<S> | undefined;
    try {
        storage = getStorage();
    } catch {
        // prevent error if the storage is not defined (e.g. when server side rendering a page)
        return;
    }

    if (!storage) {
        return;
    }

    const persistStorage: PersistStorage<S> = {
        getItem: name => storage.getItem(name),
        setItem: (name, newValue) => {
            const interrupt = options?.interrupt ?? (() => false);
            if (interrupt(name, newValue)) {
                return;
            }
            storage.setItem(name, newValue);
        },
        removeItem: name => storage.removeItem(name),
    };

    return persistStorage;
}

export const useCookiePreferencesStore = create(
    persist(
        combine(DEFAULT_STATE, set => ({
            acceptPolicy: () => set(() => ({ isPolicyAccepted: true })),
            setCookie(cookie: CookieKind, value: boolean) {
                set(state => {
                    const cookieConfig = state.cookies[cookie];

                    if (!cookieConfig) {
                        throw new Error(`Cookie "${cookie}" does not exist.`);
                    }

                    if (cookieConfig.disabled) {
                        throw new Error(`Cookie "${cookie}" is disabled and cannot be set.`);
                    }
                    return { cookies: { ...state.cookies, [cookie]: { ...cookieConfig, value } } };
                });
            },
        })),
        {
            name: COOKIE_PREFERENCES_KEY,
            storage: createInterruptibleStorage(() => createJSONStorage<typeof DEFAULT_STATE>(() => localStorage, {}), {
                interrupt: (name, value) => {
                    if (name === COOKIE_PREFERENCES_KEY) {
                        // Prevent overwriting the cookie policy state
                        return !value.state?.isPolicyAccepted;
                    }
                    return false;
                },
            }),
        },
    ),
);
